export const PRIORITIZER_STORAGE_KEY = 'dopatize:task-prioritizer:v1';
export const MAX_PRIORITY_TASKS = 50;
export const MAX_PRIORITY_TITLE = 200;
export const MAX_PRIORITY_DRAFT = 20000;

export const comparisonCount = (count) => Math.max(0, count * (count - 1) / 2);
const pairKey = (pair) => JSON.stringify([...pair].sort());
const samePair = (first, second) => first?.length === 2 && second?.length === 2
  && first.every((id, index) => id === second[index]);

export function parsePriorityDraft(draft) {
  const seen = new Set();
  const titles = [];
  for (const line of draft.split(/\r?\n/)) {
    const title = line.trim().replace(/^(?:[-*•]\s+|\d+[.)]\s+)/, '').replace(/^\[[ xX]\]\s*/, '').trim();
    if (!title || seen.has(title.toLowerCase())) continue;
    seen.add(title.toLowerCase());
    titles.push(title);
  }
  let error = '';
  if (titles.length > MAX_PRIORITY_TASKS) error = `Use up to ${MAX_PRIORITY_TASKS} tasks per list.`;
  else if (titles.some((title) => title.length > MAX_PRIORITY_TITLE)) error = `Keep each task to ${MAX_PRIORITY_TITLE} characters.`;
  return { titles, error };
}

export const initialPrioritizer = () => ({
  version: 1, draft: '', tasks: [], decisions: [], pair: null,
  phase: 'edit', checked: [], exportFormat: 'checklist',
});

export function nextPriorityPair(tasks, decisions, previousPair = null, winner = null) {
  const compared = new Set(decisions.map((decision) => pairKey(decision.pair)));
  if (winner && previousPair?.includes(winner)) {
    const challenger = tasks.find((task) => task.id !== winner && !compared.has(pairKey([winner, task.id])));
    if (challenger) return previousPair.map((id) => id === winner ? winner : challenger.id);
  }
  // A winner that has met everyone steps aside so all remaining pairs get a turn.
  for (let first = 0; first < tasks.length; first += 1) {
    for (let second = first + 1; second < tasks.length; second += 1) {
      const pair = [tasks[first].id, tasks[second].id];
      if (!compared.has(pairKey(pair))) return pair;
    }
  }
  return null;
}

export function prioritizerReducer(state, action) {
  switch (action.type) {
    case 'draft':
      return { ...state, draft: action.value.slice(0, MAX_PRIORITY_DRAFT) };
    case 'start': {
      const { titles, error } = parsePriorityDraft(state.draft);
      if (error || !titles.length) return state;
      const tasks = titles.map((title, index) => ({ id: `priority-${index}`, title }));
      const pair = nextPriorityPair(tasks, []);
      return { ...state, tasks, decisions: [], checked: [], pair, phase: pair ? 'compare' : 'results' };
    }
    case 'choose': {
      if (state.phase !== 'compare' || !samePair(state.pair, action.pair) || !state.pair.includes(action.winner)) return state;
      const decisions = [...state.decisions, { pair: [...state.pair], winner: action.winner }];
      const pair = nextPriorityPair(state.tasks, decisions, state.pair, action.winner);
      return { ...state, decisions, pair, phase: pair ? 'compare' : 'results' };
    }
    case 'undo': {
      const previous = state.decisions[state.decisions.length - 1];
      if (!previous) return state;
      return { ...state, decisions: state.decisions.slice(0, -1), pair: previous.pair, phase: 'compare' };
    }
    case 'edit':
      return { ...state, phase: 'edit' };
    case 'cancelEdit':
      if (!state.tasks.length) return state;
      return { ...state, draft: state.tasks.map((task) => task.title).join('\n'), phase: state.pair ? 'compare' : 'results' };
    case 'check':
      if (state.phase !== 'results' || !state.tasks.some((task) => task.id === action.id)) return state;
      return { ...state, checked: state.checked.includes(action.id)
        ? state.checked.filter((id) => id !== action.id) : [...state.checked, action.id] };
    case 'format':
      if (!['checklist', 'numbered'].includes(action.value)) return state;
      return { ...state, exportFormat: action.value };
    default:
      return state;
  }
}

export function rankPriorityTasks(tasks, decisions) {
  const wins = new Map(tasks.map((task) => [task.id, 0]));
  decisions.forEach(({ winner }) => wins.set(winner, (wins.get(winner) || 0) + 1));
  const tiedWins = new Map(tasks.map((task) => [task.id, 0]));
  decisions.forEach(({ pair, winner }) => {
    if (wins.get(pair[0]) === wins.get(pair[1])) tiedWins.set(winner, tiedWins.get(winner) + 1);
  });
  // Numeric scores keep sorting deterministic even when preferences form a cycle.
  return tasks.map((task, index) => ({ ...task, wins: wins.get(task.id), tiedWins: tiedWins.get(task.id), index }))
    .sort((first, second) => second.wins - first.wins || second.tiedWins - first.tiedWins || first.index - second.index);
}

export function priorityListText(tasks, checked, format = 'checklist') {
  return tasks.map((task, index) => format === 'numbered' ? `${index + 1}. ${task.title}`
    : `- [${checked.includes(task.id) ? 'x' : ' '}] ${task.title}`).join('\n');
}

export function restorePrioritizer(saved) {
  const initial = initialPrioritizer();
  if (!saved || saved.version !== 1) return initial;
  const draft = typeof saved.draft === 'string' ? saved.draft.slice(0, MAX_PRIORITY_DRAFT) : '';
  const tasks = saved.tasks;
  if (!Array.isArray(tasks) || tasks.length > MAX_PRIORITY_TASKS
    || tasks.some((task) => !task || typeof task.id !== 'string' || task.id.length > 80 || !/^priority-\d+$/.test(task.id)
      || typeof task.title !== 'string' || !task.title.trim() || task.title.length > MAX_PRIORITY_TITLE || /[\r\n]/.test(task.title))
    || new Set(tasks.map((task) => task.id)).size !== tasks.length
    || new Set(tasks.map((task) => task.title.trim().toLowerCase())).size !== tasks.length) return { ...initial, draft };
  const cleanTasks = tasks.map(({ id, title }) => ({ id, title: title.trim() }));
  let state = { ...initial, draft, tasks: cleanTasks, pair: nextPriorityPair(cleanTasks, []), phase: 'compare' };
  const decisions = Array.isArray(saved.decisions) ? saved.decisions.slice(0, comparisonCount(tasks.length)) : [];
  for (const decision of decisions) {
    if (!decision || !samePair(state.pair, decision.pair) || !state.pair.includes(decision.winner)) break;
    state = prioritizerReducer(state, { type: 'choose', pair: decision.pair, winner: decision.winner });
  }
  state.phase = !tasks.length || saved.phase === 'edit' ? 'edit' : state.pair ? 'compare' : 'results';
  state.checked = Array.isArray(saved.checked)
    ? [...new Set(saved.checked.filter((id) => cleanTasks.some((task) => task.id === id)))] : [];
  state.exportFormat = saved.exportFormat === 'numbered' ? 'numbered' : 'checklist';
  return state;
}
